import React, { useEffect, useMemo, useState } from "react";

type TestCategory =
  | "All"
  | "JEE"
  | "NEET"
  | "Boards"
  | "Foundation"
  | "Chapter Test";

type TestStatus = "Live Test" | "Upcoming" | "Available";

type QuizLevel = "Easy" | "Medium" | "Hard";

type TestSeries = {
  id: number;
  title: string;
  seoTitle: string;
  category: Exclude<TestCategory, "All">;
  status: TestStatus;
  subject: string;
  questions: number;
  marks: number;
  duration: string;
  date: string;
  level: string;
  description: string;
  image: string;
  imageAlt: string;
  points: string[];
  keywords: string;
};

type QuizItem = {
  id: number;
  title: string;
  seoTitle: string;
  subject: string;
  level: QuizLevel;
  questions: number;
  time: string;
  accuracy: string;
  keywords: string;
};

type Feature = {
  title: string;
  desc: string;
  keywords: string;
};

type AnalyticsItem = {
  title: string;
  desc: string;
  keywords: string;
};

type TestFlow = {
  step: string;
  title: string;
  desc: string;
  keywords: string;
};

const categories: TestCategory[] = [
  "All",
  "JEE",
  "NEET",
  "Boards",
  "Foundation",
  "Chapter Test",
];

const categorySeoText: Record<TestCategory, string> = {
  All: "Online test series, daily quizzes, chapter tests, full mock tests, board-style tests, JEE mock tests, NEET practice tests and foundation assessments with instant reports.",
  JEE: "JEE Main and JEE Advanced test series with Physics, Chemistry, Mathematics mock tests, PYQ practice, rank analysis, accuracy report and time management tracking.",
  NEET: "NEET UG test series with Physics, Chemistry, Biology MCQ practice, NCERT-based Biology tests, full mock exams, mistake analysis and score improvement reports.",
  Boards:
    "CBSE board exam test series with Class 10 and Class 12 practice papers, answer writing practice, competency-based questions and board-style evaluation.",
  Foundation:
    "Foundation tests for Class 7, Class 8, Class 9 and Class 10 students with Maths, Science, reasoning, worksheets, speed practice and concept checks.",
  "Chapter Test":
    "Chapter-wise tests for Physics, Chemistry, Biology, Maths, Science and CBSE NCERT topics with quick evaluation and solution explanation.",
};

const testSeries: TestSeries[] = [
  {
    id: 1,
    title: "JEE Main Full Mock Test",
    seoTitle:
      "JEE Main Full Mock Test for Physics Chemistry Mathematics with Rank Analysis",
    category: "JEE",
    status: "Live Test",
    subject: "Physics, Chemistry, Maths",
    questions: 90,
    marks: 300,
    duration: "3 Hours",
    date: "Today, 07:00 PM",
    level: "Advanced",
    description:
      "A complete JEE Main pattern test designed to improve speed, accuracy, exam temperament and problem-solving confidence with detailed performance analysis.",
    image:
      "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=900&h=650&fit=crop&auto=format&q=80",
    imageAlt:
      "JEE Main full mock test for Physics Chemistry Mathematics rank analysis and exam practice",
    points: [
      "JEE pattern questions",
      "All India ranking",
      "Detailed solution report",
    ],
    keywords:
      "JEE Main mock test, JEE test series, Physics Chemistry Maths mock test, JEE rank analysis, IIT JEE practice test, JEE Main online test",
  },
  {
    id: 2,
    title: "NEET Biology Grand Test",
    seoTitle:
      "NEET Biology Grand Test with NCERT Line-by-Line MCQ Practice",
    category: "NEET",
    status: "Upcoming",
    subject: "Biology",
    questions: 100,
    marks: 400,
    duration: "90 Minutes",
    date: "Tomorrow, 06:00 PM",
    level: "NCERT Based",
    description:
      "A NEET Biology test focused on NCERT line-by-line concepts, diagrams, application-based MCQs, accuracy building and mistake tracking.",
    image:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=900&h=650&fit=crop&auto=format&q=80",
    imageAlt:
      "NEET Biology grand test with NCERT line-by-line revision diagrams and MCQ practice",
    points: ["NCERT focused", "Chapter-wise analysis", "Mistake tracking"],
    keywords:
      "NEET Biology test, NEET Biology MCQ, NCERT Biology test, NEET UG test series, Biology mock test, medical entrance test",
  },
  {
    id: 3,
    title: "Class 10 Science Board Test",
    seoTitle:
      "Class 10 Science Board Test for CBSE NCERT Exam Preparation",
    category: "Boards",
    status: "Available",
    subject: "Science",
    questions: 45,
    marks: 80,
    duration: "2 Hours",
    date: "Available Now",
    level: "Board Level",
    description:
      "A board-style test with short answers, long answers, diagrams and competency-based questions for better Class 10 CBSE Science preparation.",
    image:
      "https://images.unsplash.com/photo-1509062522246-3755977927d7?w=900&h=650&fit=crop&auto=format&q=80",
    imageAlt:
      "Class 10 Science CBSE board test with diagrams answer writing and NCERT questions",
    points: ["Board pattern", "Answer writing practice", "Chapter coverage"],
    keywords:
      "Class 10 Science test, CBSE board test, Class 10 Science mock test, NCERT Science questions, board exam practice, competency based questions",
  },
  {
    id: 4,
    title: "Foundation Maths Speed Test",
    seoTitle:
      "Foundation Maths Speed Test for Class 7 Class 8 and Class 9 Students",
    category: "Foundation",
    status: "Available",
    subject: "Mathematics",
    questions: 50,
    marks: 100,
    duration: "60 Minutes",
    date: "Available Now",
    level: "Class 7 to 9",
    description:
      "A foundation-level test that helps students improve calculation speed, concept clarity and confidence in Mathematics through regular practice.",
    image:
      "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=900&h=650&fit=crop&auto=format&q=80",
    imageAlt:
      "Foundation Maths speed test for Class 7 Class 8 Class 9 calculation practice and concept clarity",
    points: ["Speed practice", "Concept check", "Instant score"],
    keywords:
      "Foundation Maths test, Class 7 Maths test, Class 8 Maths practice test, Class 9 Maths quiz, Maths speed test, calculation practice",
  },
  {
    id: 5,
    title: "Chemistry Chapter Test",
    seoTitle:
      "Chemistry Chapter Test for Formula Practice Concept Check and Solutions",
    category: "Chapter Test",
    status: "Upcoming",
    subject: "Chemistry",
    questions: 35,
    marks: 140,
    duration: "45 Minutes",
    date: "Friday, 05:00 PM",
    level: "Chapter Based",
    description:
      "A focused Chemistry chapter test designed to check concept understanding, formula usage, reaction practice and question-solving ability.",
    image:
      "https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=900&h=650&fit=crop&auto=format&q=80",
    imageAlt:
      "Chemistry chapter test for formula usage reaction practice concept check and solutions",
    points: ["Chapter-focused", "Quick evaluation", "Solution explanation"],
    keywords:
      "Chemistry chapter test, Chemistry formula practice, Chemistry MCQ test, NEET Chemistry test, JEE Chemistry practice, chapter wise test",
  },
  {
    id: 6,
    title: "NEET Physics Practice Test",
    seoTitle:
      "NEET Physics Practice Test for Numericals Formula Application and Accuracy",
    category: "NEET",
    status: "Available",
    subject: "Physics",
    questions: 45,
    marks: 180,
    duration: "60 Minutes",
    date: "Available Now",
    level: "Moderate",
    description:
      "A NEET Physics practice test for improving numerical accuracy, formula application, speed, time management and exam confidence.",
    image:
      "https://images.unsplash.com/photo-1509228627152-72ae9ae6848d?w=900&h=650&fit=crop&auto=format&q=80",
    imageAlt:
      "NEET Physics practice test for numericals formula application accuracy and time management",
    points: ["Numerical practice", "Formula-based MCQs", "Accuracy report"],
    keywords:
      "NEET Physics test, Physics numerical practice, NEET Physics MCQ, formula based MCQs, Physics mock test, NEET practice test",
  },
];

const quizItems: QuizItem[] = [
  {
    id: 1,
    title: "Daily Physics Quiz",
    seoTitle:
      "Daily Physics Quiz for Motion Force Numericals and Quick Revision",
    subject: "Motion & Force",
    level: "Medium",
    questions: 15,
    time: "15 min",
    accuracy: "82%",
    keywords:
      "daily Physics quiz, Motion quiz, Force quiz, Physics numericals, quick Physics revision",
  },
  {
    id: 2,
    title: "NCERT Biology Quiz",
    seoTitle:
      "NCERT Biology Quiz for Human Physiology and NEET MCQ Practice",
    subject: "Human Physiology",
    level: "Easy",
    questions: 20,
    time: "18 min",
    accuracy: "88%",
    keywords:
      "NCERT Biology quiz, Human Physiology quiz, NEET Biology MCQ, Biology quick revision",
  },
  {
    id: 3,
    title: "Maths Formula Drill",
    seoTitle:
      "Maths Formula Drill for Algebra Trigonometry Speed and Accuracy",
    subject: "Algebra & Trigonometry",
    level: "Hard",
    questions: 25,
    time: "25 min",
    accuracy: "74%",
    keywords:
      "Maths formula drill, Algebra quiz, Trigonometry quiz, Maths speed practice, formula revision",
  },
];

const features: Feature[] = [
  {
    title: "Quiz Practice",
    desc: "Short quizzes help students revise topics quickly, build regular practice habits and improve confidence before bigger tests.",
    keywords:
      "daily quiz practice, quick revision quiz, student quiz practice, topic revision",
  },
  {
    title: "Test Series",
    desc: "Full-length and chapter-wise tests are available for JEE, NEET, CBSE boards and foundation classes.",
    keywords:
      "online test series, JEE test series, NEET test series, board exam test series, chapter wise test",
  },
  {
    title: "Smart Reports",
    desc: "Students can review accuracy, time usage, weak topics, mistakes and question-wise performance after each test.",
    keywords:
      "smart test reports, accuracy analysis, weak topic detection, question wise analysis",
  },
  {
    title: "Leaderboard",
    desc: "Rank comparison keeps students motivated and helps them understand their preparation level among peers.",
    keywords:
      "leaderboard, rank comparison, percentile report, student ranking system",
  },
];

const analytics: AnalyticsItem[] = [
  {
    title: "Score Summary",
    desc: "Students can quickly understand total score, attempted questions and final marks.",
    keywords: "score summary, total marks, test score report",
  },
  {
    title: "Accuracy Analysis",
    desc: "Accuracy reports help students understand correct, incorrect and skipped questions.",
    keywords: "accuracy analysis, correct incorrect review, skipped questions",
  },
  {
    title: "Time Spent Per Question",
    desc: "Time tracking helps students improve speed and avoid spending too much time on one question.",
    keywords: "time per question, test time analysis, speed improvement",
  },
  {
    title: "Weak Topic Detection",
    desc: "Weak topics are identified so students can plan revision more effectively.",
    keywords: "weak topic detection, topic analysis, revision planning",
  },
  {
    title: "Correct vs Incorrect Review",
    desc: "Students can review mistakes and learn from detailed solutions after the test.",
    keywords: "mistake review, correct vs incorrect, solution explanation",
  },
  {
    title: "Rank & Percentile",
    desc: "Rank and percentile reports help students understand their performance level.",
    keywords: "rank report, percentile report, All India rank, student ranking",
  },
  {
    title: "Solution Explanation",
    desc: "Detailed solutions help students understand the right method and avoid repeated errors.",
    keywords: "detailed solution, solution explanation, answer explanation",
  },
  {
    title: "Retest Suggestions",
    desc: "Students can revise weak chapters and retake practice tests for improvement.",
    keywords: "retest suggestion, practice again, score improvement",
  },
];

const testFlow: TestFlow[] = [
  {
    step: "01",
    title: "Choose Test",
    desc: "Select a quiz, chapter test, board test, JEE mock test or NEET practice test according to your goal.",
    keywords:
      "choose online test, quiz selection, JEE mock test, NEET practice test, board test",
  },
  {
    step: "02",
    title: "Attempt Online",
    desc: "Solve questions within the time limit and practice real exam speed, focus and accuracy.",
    keywords:
      "attempt online test, timed test, exam speed, accuracy practice, online exam",
  },
  {
    step: "03",
    title: "Check Report",
    desc: "Review score, accuracy, mistakes, time usage, weak topics, rank and solution explanations.",
    keywords:
      "test report, score analysis, mistake analysis, weak topic report, solution explanation",
  },
  {
    step: "04",
    title: "Improve Score",
    desc: "Use the report to revise weak chapters, reattempt practice tests and improve performance step by step.",
    keywords:
      "improve score, revise weak topics, reattempt test, performance improvement",
  },
];

const seoKeywords = [
  "Mediit Academy real test",
  "online test series",
  "daily quizzes",
  "quiz practice for students",
  "JEE mock test",
  "JEE Main test series",
  "JEE Advanced test series",
  "NEET mock test",
  "NEET UG test series",
  "NEET Biology test",
  "NEET Physics test",
  "CBSE board test",
  "Class 10 Science test",
  "Class 12 board test",
  "foundation test",
  "Class 7 Maths test",
  "Class 8 Maths test",
  "Class 9 Science test",
  "chapter wise test",
  "online mock test",
  "rank analysis",
  "score report",
  "accuracy analysis",
  "weak topic detection",
  "solution explanation",
  "test dashboard",
  "student performance tracking",
].join(", ");

const getStatusStyle = (status: TestStatus) => {
  if (status === "Live Test") return "bg-red-50 text-red-600 border-red-200";
  if (status === "Upcoming") return "bg-blue-50 text-blue-600 border-blue-200";
  return "bg-green-50 text-green-600 border-green-200";
};

const getButtonText = (status: TestStatus) => {
  if (status === "Live Test") return "Start Live Test";
  if (status === "Upcoming") return "Set Reminder";
  return "Attempt Test";
};

const getLevelStyle = (level: QuizLevel) => {
  if (level === "Easy") return "bg-green-50 text-green-600 border-green-100";
  if (level === "Medium") return "bg-blue-50 text-blue-600 border-blue-100";
  return "bg-orange-50 text-orange-600 border-orange-100";
};

const RealTest: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<TestCategory>("All");

  const filteredTests = useMemo(() => {
    if (activeCategory === "All") return testSeries;
    return testSeries.filter((test) => test.category === activeCategory);
  }, [activeCategory]);

  useEffect(() => {
    const title =
      activeCategory === "All"
        ? "Real Test | JEE, NEET, CBSE Mock Tests, Quizzes and Reports - Mediit Academy"
        : `${activeCategory} Test Series | Mock Tests, Quizzes and Reports - Mediit Academy`;

    const description =
      activeCategory === "All"
        ? "Attempt Mediit Academy online test series, daily quizzes, JEE mock tests, NEET practice tests, CBSE board tests, chapter-wise tests, instant solutions, rank analysis and performance reports."
        : categorySeoText[activeCategory];

    const keywords = [
      seoKeywords,
      categorySeoText[activeCategory],
      `${activeCategory} test series`,
      `${activeCategory} mock test`,
      `${activeCategory} online test`,
      `${activeCategory} quiz practice`,
    ].join(", ");

    document.title = title;

    const setMetaTag = (name: string, content: string) => {
      let tag = document.querySelector(
        `meta[name="${name}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    const setPropertyTag = (property: string, content: string) => {
      let tag = document.querySelector(
        `meta[property="${property}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    setMetaTag("description", description);
    setMetaTag("keywords", keywords);
    setMetaTag("robots", "index, follow");
    setMetaTag("author", "Mediit Academy");

    setPropertyTag("og:title", title);
    setPropertyTag("og:description", description);
    setPropertyTag("og:type", "website");
    setPropertyTag("og:site_name", "Mediit Academy");

    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", title);
    setMetaTag("twitter:description", description);

    let canonical = document.querySelector(
      `link[rel="canonical"]`
    ) as HTMLLinkElement | null;

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute("href", `${window.location.origin}/real-test`);
  }, [activeCategory]);

  const testSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Mediit Academy Real Test",
    description:
      "Online test series, daily quizzes, chapter-wise tests, JEE mock tests, NEET practice tests, CBSE board tests and performance reports.",
    url: "/real-test",
    keywords: seoKeywords,
    mainEntity: {
      "@type": "ItemList",
      name: "Online Test Series and Quizzes",
      itemListElement: testSeries.map((test, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "LearningResource",
          name: test.seoTitle,
          description: test.description,
          learningResourceType: "Online Test",
          educationalLevel: test.level,
          about: test.subject,
          keywords: test.keywords,
          provider: {
            "@type": "EducationalOrganization",
            name: "Mediit Academy",
            url: "/",
          },
        },
      })),
    },
  };

  const quizSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Daily Quiz Practice",
    description:
      "Daily quiz practice for Physics, Biology, Maths and quick revision.",
    itemListElement: quizItems.map((quiz, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "LearningResource",
        name: quiz.seoTitle,
        description: `${quiz.title} contains ${quiz.questions} questions and can be completed in ${quiz.time}.`,
        learningResourceType: "Quiz",
        about: quiz.subject,
        keywords: quiz.keywords,
      },
    })),
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How Students Improve with Mediit Real Test",
    description:
      "A simple process where students choose a test, attempt it online, check reports and improve scores through revision.",
    step: testFlow.map((item) => ({
      "@type": "HowToStep",
      name: item.title,
      text: item.desc,
    })),
  };

  const goToTestDashboard = () => {
    window.location.href = "/test-dashboard";
  };

  const goToQuizZone = () => {
    window.location.href = "/quiz-zone";
  };

  const scrollToTests = () => {
    const section = document.getElementById("test-series");
    section?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToQuiz = () => {
    const section = document.getElementById("quiz-zone");
    section?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#f6f8fc] font-['Inter'] text-[#101827]">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(testSchema),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(quizSchema),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(howToSchema),
        }}
      />

      <main>
        {/* HERO */}
        <section
          className="relative overflow-hidden bg-[#f6f8fc]"
          aria-labelledby="real-test-heading"
        >
          <div className="absolute -left-20 top-10 h-80 w-80 rounded-full bg-blue-200/50 blur-3xl" />
          <div className="absolute right-0 top-20 h-96 w-96 rounded-full bg-purple-200/40 blur-3xl" />
          <div className="absolute bottom-0 left-[45%] h-72 w-72 rounded-full bg-orange-100 blur-3xl" />

          <div className="relative mx-auto max-w-[1380px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
            <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
              <div className="lg:col-span-6">
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-4 py-2 shadow-sm">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-xs font-black text-white">
                    RT
                  </span>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">
                    Mediit Real Test
                  </p>
                </div>

                <h1
                  id="real-test-heading"
                  className="mt-6 max-w-4xl text-4xl font-black leading-tight tracking-[-0.05em] text-slate-950 sm:text-5xl lg:text-[64px]"
                >
                  Online Test Series, Daily Quizzes and Reports for Real Exam
                  Practice
                </h1>

                <p className="mt-5 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">
                  Students can attempt daily quizzes, chapter tests, full mock
                  tests and board-style papers with instant results, detailed
                  solutions, rank comparison, weak topic detection and topic-wise
                  performance analysis.
                </p>

                <p className="sr-only">{seoKeywords}</p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={goToTestDashboard}
                    aria-label="Open Mediit Academy online test dashboard"
                    className="rounded-2xl bg-[#101827] px-7 py-4 text-sm font-black text-white shadow-xl shadow-slate-900/15 transition-all duration-300 hover:-translate-y-1 hover:bg-slate-800"
                  >
                    Open Test Dashboard
                  </button>

                  <button
                    type="button"
                    onClick={scrollToQuiz}
                    aria-label="Try daily quiz zone for quick revision"
                    className="rounded-2xl border border-slate-200 bg-white px-7 py-4 text-sm font-black text-slate-950 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-300"
                  >
                    Try Quiz Zone
                  </button>
                </div>

                <div
                  className="mt-9 grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-3"
                  aria-label="Real test highlights"
                >
                  <article className="rounded-3xl border border-blue-100 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                    <p className="text-3xl font-black text-blue-600">120+</p>
                    <p className="mt-1 text-xs font-bold text-slate-500">
                      Practice Tests
                    </p>
                  </article>

                  <article className="rounded-3xl border border-green-100 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                    <p className="text-3xl font-black text-green-600">Daily</p>
                    <p className="mt-1 text-xs font-bold text-slate-500">
                      Quiz Practice
                    </p>
                  </article>

                  <article className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                    <p className="text-3xl font-black text-orange-600">AIR</p>
                    <p className="mt-1 text-xs font-bold text-slate-500">
                      Ranking System
                    </p>
                  </article>
                </div>
              </div>

              <div className="lg:col-span-6">
                <div className="relative mx-auto max-w-[590px]">
                  <div className="absolute -left-4 top-12 z-10 hidden rounded-3xl border border-slate-100 bg-white p-4 shadow-2xl md:block">
                    <p className="text-xs font-bold text-slate-500">
                      Today Quiz
                    </p>
                    <p className="mt-1 text-2xl font-black text-blue-600">
                      15 Min
                    </p>
                  </div>

                  <div className="absolute -right-4 bottom-20 z-10 hidden rounded-3xl border border-slate-100 bg-white p-4 shadow-2xl md:block">
                    <p className="text-xs font-bold text-slate-500">Streak</p>
                    <p className="mt-1 text-2xl font-black text-orange-600">
                      07 Days
                    </p>
                  </div>

                  <div className="rounded-[36px] border border-white bg-white p-5 shadow-[0_30px_90px_rgba(15,23,42,0.14)]">
                    <div className="rounded-[30px] bg-[#101827] p-5 text-white">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-xs font-black uppercase tracking-[0.16em] text-blue-200">
                            Quiz Zone
                          </p>
                          <h2 className="mt-2 text-2xl font-black">
                            Daily Practice Challenge
                          </h2>
                        </div>

                        <span className="w-fit rounded-full bg-green-400/10 px-4 py-2 text-xs font-black text-green-300">
                          Active Now
                        </span>
                      </div>

                      <div className="mt-6 rounded-3xl bg-white p-5 text-slate-950">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <p className="text-xs font-black uppercase tracking-[0.14em] text-blue-600">
                              Physics Quiz
                            </p>
                            <h3 className="mt-1 text-2xl font-black">
                              Laws of Motion
                            </h3>
                            <p className="mt-2 text-sm text-slate-500">
                              15 questions · 15 minutes · instant solutions
                            </p>
                          </div>

                          <div className="rounded-2xl bg-blue-50 p-4 text-center">
                            <p className="text-2xl font-black text-blue-600">
                              82%
                            </p>
                            <p className="text-xs font-bold text-slate-500">
                              Accuracy
                            </p>
                          </div>
                        </div>

                        <div className="mt-5 grid grid-cols-3 gap-3">
                          <div className="rounded-2xl bg-slate-50 p-4 text-center">
                            <p className="text-xl font-black">15</p>
                            <p className="text-[11px] font-bold text-slate-500">
                              Questions
                            </p>
                          </div>

                          <div className="rounded-2xl bg-slate-50 p-4 text-center">
                            <p className="text-xl font-black">60</p>
                            <p className="text-[11px] font-bold text-slate-500">
                              Marks
                            </p>
                          </div>

                          <div className="rounded-2xl bg-slate-50 p-4 text-center">
                            <p className="text-xl font-black">15m</p>
                            <p className="text-[11px] font-bold text-slate-500">
                              Time
                            </p>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={goToQuizZone}
                          aria-label="Start daily Physics quiz"
                          className="mt-5 w-full rounded-2xl bg-blue-600 px-5 py-4 text-sm font-black text-white transition-all duration-300 hover:-translate-y-1 hover:bg-blue-700"
                        >
                          Start Daily Quiz
                        </button>
                      </div>

                      <div className="mt-4 grid grid-cols-2 gap-3">
                        <div className="rounded-2xl bg-white/10 p-4">
                          <p className="text-2xl font-black">18</p>
                          <p className="text-xs text-white/60">
                            Quizzes Completed
                          </p>
                        </div>

                        <div className="rounded-2xl bg-white/10 p-4">
                          <p className="text-2xl font-black">04</p>
                          <p className="text-xs text-white/60">
                            Tests Pending
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="absolute -bottom-6 left-8 right-8 hidden rounded-3xl border border-slate-100 bg-white p-4 shadow-xl sm:block">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-black text-slate-950">
                          Next Mock Test
                        </p>
                        <p className="mt-1 text-xs font-bold text-slate-500">
                          JEE Main Full Test · Today 07:00 PM
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={scrollToTests}
                        aria-label="View upcoming JEE Main full mock test"
                        className="rounded-xl bg-slate-950 px-4 py-2 text-xs font-black text-white"
                      >
                        View
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FEATURE STRIP */}
        <section className="bg-white py-10">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {features.map((item, index) => (
                <article
                  key={item.title}
                  title={item.keywords}
                  className="group rounded-[28px] border border-slate-100 bg-[#f8fbff] p-6 transition-all duration-300 hover:-translate-y-2 hover:bg-white hover:shadow-xl"
                >
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-lg font-black text-blue-600 shadow-sm transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white">
                    {String(index + 1).padStart(2, "0")}
                  </div>

                  <h3 className="text-xl font-black text-slate-950">
                    {item.title}
                  </h3>

                  <p className="mt-3 text-sm leading-6 text-slate-500">
                    {item.desc}
                  </p>

                  <p className="sr-only">{item.keywords}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* TEST FLOW */}
        <section className="bg-[#f6f8fc] py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="mb-9 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                  Test Practice Flow
                </p>
                <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] text-slate-950 sm:text-4xl">
                  Practice, Analyze and Improve with Every Test
                </h2>
              </div>

              <p className="max-w-xl text-sm leading-6 text-slate-500">
                Every test is designed to help students understand their
                preparation level and take the next right step for improvement.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {testFlow.map((item) => (
                <article
                  key={item.step}
                  title={item.keywords}
                  className="rounded-[28px] border border-slate-100 bg-white p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
                >
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#101827] text-lg font-black text-white">
                    {item.step}
                  </div>

                  <h3 className="text-xl font-black text-slate-950">
                    {item.title}
                  </h3>

                  <p className="mt-3 text-sm leading-6 text-slate-500">
                    {item.desc}
                  </p>

                  <p className="sr-only">{item.keywords}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* QUIZ ZONE */}
        <section id="quiz-zone" className="bg-white py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                  Quiz Zone
                </p>
                <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] text-slate-950 sm:text-4xl">
                  Quick Quizzes for Daily Revision
                </h2>
              </div>

              <p className="max-w-xl text-sm leading-6 text-slate-500">
                Daily quizzes help students revise topics faster, build speed,
                improve accuracy and stay consistent before attempting full
                mock tests.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
              {quizItems.map((quiz) => (
                <article
                  key={quiz.id}
                  title={quiz.keywords}
                  className="group rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:border-blue-200 hover:shadow-[0_22px_60px_rgba(15,23,42,0.12)]"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-xl font-black text-blue-600 transition-all group-hover:bg-blue-600 group-hover:text-white">
                      Q
                    </div>

                    <span
                      className={`rounded-full border px-3 py-1 text-xs font-black ${getLevelStyle(
                        quiz.level
                      )}`}
                    >
                      {quiz.level}
                    </span>
                  </div>

                  <h3 className="mt-5 text-2xl font-black tracking-[-0.03em] text-slate-950">
                    {quiz.title}
                  </h3>

                  <p className="mt-2 text-sm font-bold text-blue-700">
                    {quiz.seoTitle}
                  </p>

                  <p className="mt-2 text-sm font-bold text-slate-500">
                    {quiz.subject}
                  </p>

                  <p className="sr-only">{quiz.keywords}</p>

                  <div className="mt-5 grid grid-cols-3 gap-3">
                    <div className="rounded-2xl bg-slate-50 p-3 text-center">
                      <p className="text-lg font-black text-slate-950">
                        {quiz.questions}
                      </p>
                      <p className="text-[11px] font-bold text-slate-500">
                        Questions
                      </p>
                    </div>

                    <div className="rounded-2xl bg-slate-50 p-3 text-center">
                      <p className="text-lg font-black text-slate-950">
                        {quiz.time}
                      </p>
                      <p className="text-[11px] font-bold text-slate-500">
                        Time
                      </p>
                    </div>

                    <div className="rounded-2xl bg-slate-50 p-3 text-center">
                      <p className="text-lg font-black text-green-600">
                        {quiz.accuracy}
                      </p>
                      <p className="text-[11px] font-bold text-slate-500">
                        Avg.
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={goToQuizZone}
                    aria-label={`Start ${quiz.seoTitle}`}
                    className="mt-6 w-full rounded-2xl bg-[#101827] px-5 py-3 text-sm font-black text-white transition-all duration-300 hover:-translate-y-1 hover:bg-slate-800"
                  >
                    Start Quiz
                  </button>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* TEST SERIES */}
        <section id="test-series" className="bg-[#f6f8fc] py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="mb-7 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                  Test Series
                </p>
                <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] text-slate-950 sm:text-4xl">
                  Choose Test Series by Goal and Subject
                </h2>
              </div>

              <p className="max-w-xl text-sm leading-6 text-slate-500">
                Filter by JEE, NEET, board exams, foundation level and
                chapter-based practice tests according to your learning goal.
              </p>
            </div>

            <div
              className="mb-8 flex gap-3 overflow-x-auto pb-2"
              aria-label="Test category filters"
            >
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActiveCategory(category)}
                  title={categorySeoText[category]}
                  aria-label={`Show ${category} tests. ${categorySeoText[category]}`}
                  className={`shrink-0 rounded-full border px-5 py-2.5 text-sm font-black transition-all duration-300 ${
                    activeCategory === category
                      ? "border-[#101827] bg-[#101827] text-white shadow-lg shadow-slate-900/15"
                      : "border-slate-200 bg-white text-slate-700 hover:-translate-y-0.5 hover:border-blue-300 hover:text-blue-600"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            <p className="mb-6 text-sm leading-6 text-slate-500">
              {categorySeoText[activeCategory]}
            </p>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {filteredTests.map((test) => (
                <article
                  key={test.id}
                  title={test.keywords}
                  className="group overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-2 hover:border-blue-200 hover:shadow-[0_22px_60px_rgba(15,23,42,0.12)]"
                >
                  <div className="relative h-[215px] overflow-hidden">
                    <img
                      src={test.image}
                      alt={test.imageAlt}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-950/10 to-transparent" />

                    <span
                      className={`absolute left-4 top-4 rounded-full border px-3 py-1 text-xs font-black ${getStatusStyle(
                        test.status
                      )}`}
                    >
                      {test.status}
                    </span>

                    <span className="absolute right-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-black text-slate-950 backdrop-blur">
                      {test.level}
                    </span>

                    <div className="absolute bottom-4 left-4 right-4">
                      <p className="text-xs font-black uppercase tracking-[0.14em] text-blue-200">
                        {test.category} Test
                      </p>
                      <h3 className="mt-2 text-2xl font-black leading-tight tracking-[-0.03em] text-white">
                        {test.title}
                      </h3>
                    </div>
                  </div>

                  <div className="p-5">
                    <p className="text-sm font-bold text-blue-700">
                      {test.seoTitle}
                    </p>

                    <p className="mt-2 text-sm leading-6 text-slate-500">
                      {test.description}
                    </p>

                    <p className="sr-only">{test.keywords}</p>

                    <div className="mt-5 grid grid-cols-3 gap-3">
                      <div className="rounded-2xl bg-slate-50 p-3 text-center">
                        <p className="text-lg font-black text-slate-950">
                          {test.questions}
                        </p>
                        <p className="text-[11px] font-bold text-slate-500">
                          Questions
                        </p>
                      </div>

                      <div className="rounded-2xl bg-slate-50 p-3 text-center">
                        <p className="text-lg font-black text-slate-950">
                          {test.marks}
                        </p>
                        <p className="text-[11px] font-bold text-slate-500">
                          Marks
                        </p>
                      </div>

                      <div className="rounded-2xl bg-slate-50 p-3 text-center">
                        <p className="text-lg font-black text-slate-950">
                          {test.duration}
                        </p>
                        <p className="text-[11px] font-bold text-slate-500">
                          Time
                        </p>
                      </div>
                    </div>

                    <div className="mt-5 rounded-2xl bg-blue-50 p-4">
                      <p className="text-xs font-bold text-blue-600">
                        Subject
                      </p>
                      <p className="mt-1 text-sm font-black text-slate-950">
                        {test.subject}
                      </p>
                      <p className="mt-2 text-xs font-bold text-slate-500">
                        {test.date}
                      </p>
                    </div>

                    <div className="mt-5 space-y-2">
                      {test.points.map((point) => (
                        <div key={point} className="flex items-center gap-2">
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-50 text-xs font-black text-green-600">
                            ✓
                          </span>
                          <p className="text-sm font-medium text-slate-700">
                            {point}
                          </p>
                        </div>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={goToTestDashboard}
                      aria-label={`${getButtonText(test.status)} for ${
                        test.seoTitle
                      }`}
                      className={`mt-6 w-full rounded-2xl px-5 py-3 text-sm font-black transition-all duration-300 hover:-translate-y-1 ${
                        test.status === "Live Test"
                          ? "bg-red-600 text-white shadow-lg shadow-red-600/20 hover:bg-red-700"
                          : test.status === "Upcoming"
                          ? "bg-[#101827] text-white shadow-lg shadow-slate-900/15 hover:bg-slate-800"
                          : "bg-blue-600 text-white shadow-lg shadow-blue-600/20 hover:bg-blue-700"
                      }`}
                    >
                      {getButtonText(test.status)}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ANALYTICS */}
        <section className="bg-white py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-9 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-5">
                <div className="rounded-[34px] bg-[#101827] p-7 text-white shadow-2xl lg:p-8">
                  <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-200">
                    Result Analytics
                  </p>

                  <h2 className="mt-3 text-3xl font-black tracking-[-0.04em] sm:text-4xl">
                    Every Test Becomes a Clear Improvement Plan
                  </h2>

                  <p className="mt-4 text-sm leading-7 text-white/70">
                    Students can review score, accuracy, mistakes, weak topics,
                    rank, time management and detailed solutions after each test.
                  </p>

                  <button
                    type="button"
                    onClick={goToTestDashboard}
                    aria-label="View result analytics dashboard"
                    className="mt-7 rounded-2xl bg-white px-7 py-4 text-sm font-black text-slate-950 transition-all duration-300 hover:-translate-y-1"
                  >
                    View Report Dashboard
                  </button>
                </div>
              </div>

              <div className="lg:col-span-7">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {analytics.map((item) => (
                    <article
                      key={item.title}
                      title={item.keywords}
                      className="flex items-start gap-4 rounded-[24px] border border-slate-100 bg-[#f8fbff] p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                    >
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-lg font-black text-blue-600 shadow-sm">
                        ✓
                      </span>
                      <div>
                        <h3 className="text-sm font-black text-slate-800">
                          {item.title}
                        </h3>
                        <p className="mt-2 text-xs leading-5 text-slate-500">
                          {item.desc}
                        </p>
                        <p className="sr-only">{item.keywords}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#f6f8fc] py-12">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="relative overflow-hidden rounded-[36px] bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 p-8 text-white shadow-2xl lg:p-10">
              <div className="absolute -right-10 -top-10 h-44 w-44 rounded-full bg-blue-500/30 blur-3xl" />
              <div className="absolute -bottom-10 left-10 h-44 w-44 rounded-full bg-orange-500/20 blur-3xl" />

              <div className="relative flex flex-col gap-7 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-sm font-black uppercase tracking-[0.16em] text-blue-200">
                    Practice. Analyze. Improve.
                  </p>

                  <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] sm:text-4xl">
                    Build Exam Confidence with Quizzes and Real Test Series
                  </h2>

                  <p className="mt-3 max-w-2xl text-sm leading-6 text-white/70">
                    Mediit Real Test gives students daily quiz practice, full
                    mock tests, instant scoring, topic-wise reports and smart
                    feedback for consistent improvement.
                  </p>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={goToTestDashboard}
                    aria-label="Open test dashboard"
                    className="rounded-2xl bg-white px-7 py-4 text-sm font-black text-slate-950 transition-all duration-300 hover:-translate-y-1"
                  >
                    Open Dashboard
                  </button>

                  <button
                    type="button"
                    onClick={scrollToTests}
                    aria-label="View all online test series"
                    className="rounded-2xl border border-white/15 bg-white/10 px-7 py-4 text-sm font-black text-white backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:bg-white/15"
                  >
                    View Test Series
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default RealTest; 